"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib";

export const HEX_TILE_PATTERN_LIGHT_SRC = "/images/hex-tile.png";
export const HEX_TILE_PATTERN_DARK_SRC = "/images/hex-tile-white.png";

const HEX_TILE_BACKGROUND_OPAQUE_BASE_CLASS = "bg-white dark:bg-[#111111]";

/** Opaque base + hex wash strength for panels and learn right sidebar. */
export const HEX_TILE_BACKGROUND_SUBTLE_WASH_OPACITY = 0.5;

export interface HexTileBackgroundProps {
	children: ReactNode;
	className?: string;
	contentClassName?: string;
	/** When true (default), stretch the wash to at least the scrollport height. */
	fillContainer?: boolean;
	/**
	 * Opaque base under the hex wash (e.g. scoped panel sheets). Use with
	 * `washOpacity` so the sheet stays readable while the pattern is subdued.
	 */
	opaqueBase?: boolean;
	/**
	 * Opacity of the hex-tile wash layer group (0–1), above `opaqueBase` only.
	 * Tile PNGs keep their default subtle opacities inside the group.
	 */
	washOpacity?: number;
	/**
	 * Opacity of the entire absolute background stack (opaque base + wash).
	 * Use to fade the hextile away and reveal a layer behind (e.g. glass blur).
	 */
	backgroundOpacity?: number;
	/** Merged onto the absolute background stack wrapper. */
	backgroundClassName?: string;
	/** Stable selector for Playwright / DOM smoke. */
	"data-testid"?: string;
}

/**
 * Gradient + repeating hex-tile wash (light/dark pattern PNGs), matching `HexagonMapBackdrop`.
 * Background layers are absolutely positioned so only in-flow content drives height (shrinks when
 * accordions collapse). `min-h-full` fills short content to the scrollport when `fillContainer`.
 */
export function HexTileBackground({
	children,
	className,
	contentClassName,
	fillContainer = true,
	opaqueBase = false,
	washOpacity,
	backgroundOpacity,
	backgroundClassName,
	"data-testid": dataTestId,
}: HexTileBackgroundProps) {
	const minHeightClass = fillContainer ? "min-h-full" : "min-h-0";

	return (
		<div
			data-testid={dataTestId}
			className={cn(
				"relative flex w-full flex-col",
				minHeightClass,
				className,
			)}
		>
			<div
				className={cn(
					"pointer-events-none absolute inset-0 overflow-hidden",
					backgroundClassName,
				)}
				style={
					backgroundOpacity != null
						? { opacity: backgroundOpacity }
						: undefined
				}
				aria-hidden
			>
				{opaqueBase ? (
					<div
						className={cn(
							"absolute inset-0",
							HEX_TILE_BACKGROUND_OPAQUE_BASE_CLASS,
						)}
					/>
				) : (
					<div className="absolute inset-0 bg-white dark:bg-[#111111] bg-gradient-to-b from-[#ffffff]/50 to-[#DAD3DF]/50 dark:from-[#111111]/50 dark:to-[var(--sq-grey-static-from)]/50" />
				)}
				<div
					className="absolute inset-0"
					style={
						washOpacity != null
							? { opacity: washOpacity }
							: undefined
					}
				>
					<div
						className="absolute inset-0 bg-repeat bg-[length:6px] opacity-[0.12] dark:hidden"
						style={{
							backgroundImage: `url(${HEX_TILE_PATTERN_LIGHT_SRC})`,
						}}
					/>
					<div
						className="absolute inset-0 hidden bg-repeat bg-[length:6px] opacity-[0.06] dark:block"
						style={{
							backgroundImage: `url(${HEX_TILE_PATTERN_DARK_SRC})`,
						}}
					/>
				</div>
			</div>
			<div className={cn("relative z-10", contentClassName)}>
				{children}
			</div>
		</div>
	);
}
